import { useQuery } from '@apollo/client/react';
import { Alert, Descriptions, Flex, Modal, Table, Tag, Typography } from 'antd';

import BulkOperationResults from './BulkOperationResults';
import BulkStatusTag from './BulkStatusTag';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import { BULK_UPLOAD } from 'queries/bulkOperations';
import { formatDateTime } from 'utilities/datesAndTimes';
import {
  ROW_STATUS,
  STEP_LABEL,
  TASK_OUTCOME,
  UPLOAD_STATUS,
  csvSpecForActions,
} from './constants';

const countTags = (counts, labels, keyOf) => (
  <Flex gap="var(--spacing-sm)" wrap>
    {counts.map((count) => {
      const value = keyOf(count);
      const { label, color } = labels[value] ?? { label: value };

      return (
        <Tag key={value} color={color}>
          {`${label}: ${count.count.toLocaleString()}`}
        </Tag>
      );
    })}
  </Flex>
);

const stepColumns = [
  {
    title: 'Step',
    dataIndex: 'taskId',
    key: 'taskId',
    render: (taskId) => STEP_LABEL[taskId] ?? taskId,
  },
  {
    title: 'Outcomes',
    dataIndex: 'outcomes',
    key: 'outcomes',
    render: (outcomes) =>
      outcomes.length
        ? countTags(outcomes, TASK_OUTCOME, ({ outcome }) => outcome)
        : '—',
  },
];

/**
 * Step counts cover settled rows only, so they need not add up to the file's row
 * count while it is still running.
 */
const BulkUploadDetail = ({ uploadToken, onClose }) => {
  const uploadQuery = useQuery(BULK_UPLOAD, {
    variables: { uploadToken },
    skip: !uploadToken,
    fetchPolicy: 'cache-and-network',
  });

  const upload = uploadQuery.data?.bulkUpload;

  return (
    <Modal
      open={Boolean(uploadToken)}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden={true}
      title={upload?.filename ?? 'Upload'}
    >
      <QueryBoundary
        query={uploadQuery}
        loadingMessage="Loading this upload"
        errorMessage="Failed to load this upload"
      >
        {upload && (
          <>
            <Descriptions
              size="small"
              bordered
              column={{ xs: 1, sm: 2 }}
              items={[
                {
                  key: 'status',
                  label: 'Status',
                  children: (
                    <BulkStatusTag
                      value={upload.status}
                      labels={UPLOAD_STATUS}
                    />
                  ),
                },
                { key: 'label', label: 'Label', children: upload.label || '—' },
                {
                  key: 'insertTz',
                  label: 'Uploaded',
                  children: formatDateTime(upload.insertTz, true),
                },
                {
                  key: 'rows',
                  label: 'Results',
                  children: `${upload.rows.total.toLocaleString()} (${upload.rows.pending.toLocaleString()} not started)`,
                },
                {
                  key: 'byStatus',
                  label: 'Results by status',
                  span: 2,
                  children: upload.rows.byStatus.length
                    ? countTags(
                        upload.rows.byStatus,
                        ROW_STATUS,
                        ({ status }) => status,
                      )
                    : '—',
                },
              ]}
            />

            {upload.errorMessage && (
              <Alert
                banner
                type="error"
                title={upload.errorMessage}
                style={{ marginTop: 'var(--spacing-md)' }}
              />
            )}

            <Typography.Title
              level={5}
              style={{ marginTop: 'var(--spacing-lg)' }}
            >
              Steps
            </Typography.Title>
            <Table
              size="small"
              rowKey="taskId"
              pagination={false}
              dataSource={upload.steps}
              columns={stepColumns}
            />

            <Typography.Title
              level={5}
              style={{ marginTop: 'var(--spacing-lg)' }}
            >
              Results
            </Typography.Title>
            <BulkOperationResults
              uploadToken={uploadToken}
              filename={upload.filename}
              idColumn={csvSpecForActions(upload.requestedActions).idColumn}
            />
          </>
        )}
      </QueryBoundary>
    </Modal>
  );
};

export default BulkUploadDetail;
